import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { repositoryKeys } from '../queries/keys';
import { repositoryPaths } from '../queries/paths';

// TSS vite/03 — server state for `repositories`, their environments and their
// deployments. Three levels that chain: a repo has one environment per kind,
// and each environment has a history of deployments.

export const REPOSITORIES_PAGE_SIZE = 25;   // the backend default; hard cap is 100

// Takes project_id, kind and clone_protocol on top of limit/offset.
export function useRepositoriesList({
    limit = REPOSITORIES_PAGE_SIZE, offset = 0, projectId, kind,
} = {}) {
    const params = { limit, offset };
    if (projectId) params.project_id = projectId;
    if (kind) params.kind = kind;

    return useQuery({
        queryKey: repositoryKeys.list(params),
        queryFn: ({ signal }) => api
            .get(repositoryPaths.list(), { params, signal })
            .then(r => r.data),
        placeholderData: keepPreviousData,
    });
}

export function useRepository(id) {
    return useQuery({
        queryKey: repositoryKeys.detail(id),
        queryFn: ({ signal }) => api.get(repositoryPaths.detail(id), { signal }).then(r => r.data),
        enabled: !!id,
    });
}

export function useCreateRepository() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(repositoryPaths.list(), payload).then(r => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: repositoryKeys.all }),
    });
}

export function useUpdateRepository(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.patch(repositoryPaths.detail(id), payload).then(r => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: repositoryKeys.detail(id) });
            qc.invalidateQueries({ queryKey: repositoryKeys.all });
        },
    });
}

// ---- environments ----

export function useEnvironments(repositoryId) {
    return useQuery({
        queryKey: repositoryKeys.environments(repositoryId),
        queryFn: ({ signal }) => api
            .get(repositoryPaths.environments(repositoryId), { signal })
            .then(r => r.data),
        enabled: !!repositoryId,
    });
}

// POST /admin/repositories/{id}/environments — ONE environment per kind. The
// form only offers the kinds this repository does not have yet, because a
// second `prod` is not a thing that exists.
export function useCreateEnvironment(repositoryId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api
            .post(repositoryPaths.environments(repositoryId), payload)
            .then(r => r.data),
        onSuccess: () => qc.invalidateQueries({
            queryKey: repositoryKeys.environments(repositoryId),
        }),
    });
}

// An environment is written and removed by its OWN id, not under the repo.
export function useUpdateEnvironment(repositoryId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, ...payload }) => api
            .patch(repositoryPaths.environment(id), payload).then(r => r.data),
        onSuccess: () => qc.invalidateQueries({
            queryKey: repositoryKeys.environments(repositoryId),
        }),
    });
}

export function useDeleteEnvironment(repositoryId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => api.delete(repositoryPaths.environment(id)).then(r => r.data),
        onSuccess: () => qc.invalidateQueries({
            queryKey: repositoryKeys.environments(repositoryId),
        }),
    });
}

// ---- deployments ----

export function useDeployments(environmentId, { enabled } = {}) {
    return useQuery({
        queryKey: repositoryKeys.deployments(environmentId),
        queryFn: ({ signal }) => api
            .get(repositoryPaths.deployments(environmentId), { signal })
            .then(r => r.data),
        enabled: !!environmentId && !!enabled,
    });
}

// A deployment is IMMUTABLE: it is created and listed, never edited or
// deleted. Fixing one is recording another — which is why there is no update
// or delete mutation here, and why the API has none either.
//
// If it carries `version`, it updates the environment's `deployed_version` in
// the same transaction, so the environments list is invalidated too.
export function useCreateDeployment(environmentId, repositoryId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api
            .post(repositoryPaths.deployments(environmentId), payload).then(r => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: repositoryKeys.deployments(environmentId) });
            qc.invalidateQueries({ queryKey: repositoryKeys.environments(repositoryId) });
        },
    });
}

// Same shape as the tier picker, and for the same reason: `GET
// /admin/repositories` takes project_id, kind and clone_protocol — no `q`.
// Narrowing by project is the filter that belongs here anyway, because a task's
// repository has to be one of its project's.
export function repositoryOptions(projectId) {
    return (_q, signal) => {
        const params = { limit: 20, offset: 0 };
        if (projectId) params.project_id = projectId;
        return api
            .get(repositoryPaths.list(), { params, signal })
            .then(r => r.data.items ?? []);
    };
}

export function resolveRepository(id, signal) {
    return api
        .get(repositoryPaths.detail(id), { signal })
        .then(r => r.data?.repository ?? r.data)
        .catch(() => null);
}
